import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";

// ============================================================
// FONT
// ============================================================
//
// Put these files in:
//
// public/fonts/
//   NotoSans-Regular.ttf
//   NotoSans-SemiBold.ttf
//   NotoSans-Bold.ttf
//
// Noto Sans supports the Philippine Peso symbol: ₱
//

Font.register({
  family: "NotoSans",
  src: "/fonts/NotoSans-Regular.ttf",
});

// ============================================================
// STYLES
// ============================================================

const styles = StyleSheet.create({
  page: {
    padding: 30,
    paddingBottom: 45,
    fontSize: 9,
    fontFamily: "NotoSans",
    backgroundColor: "#ffffff",
  },

  // ----------------------------------------------------------
  // HEADER
  // ----------------------------------------------------------

  title: {
    fontSize: 18,
    color: "#1a1a2e",
    marginBottom: 3,
  },

  subtitle: {
    fontSize: 10,
    color: "#0d6efd",
    marginBottom: 18,
  },

  // ----------------------------------------------------------
  // SECTION
  // ----------------------------------------------------------

  sectionTitle: {
    fontSize: 8,
    fontWeight: 600,
    color: "#6c757d",
    letterSpacing: 0.8,
    marginBottom: 7,
  },

  // ----------------------------------------------------------
  // ACCOUNTING PERIOD
  // ----------------------------------------------------------

  periodContainer: {
    border: "1px solid #dee2e6",
    borderRadius: 6,
    padding: 12,
    marginBottom: 15,
  },

  periodRow: {
    flexDirection: "row",
  },

  periodColumn: {
    width: "50%",
  },

  label: {
    fontSize: 7,
    color: "#6c757d",
    marginBottom: 3,
  },

  value: {
    fontSize: 9,
    fontWeight: 600,
    color: "#1a1a2e",
  },

  // ----------------------------------------------------------
  // CURRENCY RATES
  // ----------------------------------------------------------

  currencySection: {
    marginTop: 12,
    paddingTop: 10,
    borderTop: "1px solid #eeeeee",
  },

  currencyRow: {
    flexDirection: "row",
    flexWrap: "wrap",
  },

  currencyBox: {
    border: "1px solid #dee2e6",
    borderRadius: 4,
    paddingVertical: 6,
    paddingHorizontal: 9,
    marginRight: 8,
    marginBottom: 5,
  },

  currencyName: {
    fontSize: 8,
    fontWeight: 600,
    color: "#212529",
  },

  currencyEquals: {
    fontSize: 8,
    color: "#6c757d",
  },

  currencyValue: {
    fontSize: 8,
    fontWeight: 700,
    color: "#0d6efd",
  },

  // ----------------------------------------------------------
  // SUMMARY
  // ----------------------------------------------------------

  summaryContainer: {
    flexDirection: "row",
    marginBottom: 15,
  },

  summaryBox: {
    width: "50%",
    border: "1px solid #dee2e6",
    borderRadius: 6,
    padding: 10,
  },

  summaryBoxLeft: {
    marginRight: 7,
  },

  summaryBoxRight: {
    marginLeft: 7,
  },

  summaryLabel: {
    fontSize: 7,
    color: "#6c757d",
    marginBottom: 4,
  },

  summaryValue: {
    fontSize: 10,
    fontWeight: 700,
    color: "#1a1a2e",
  },

  // ----------------------------------------------------------
  // TABLE
  // ----------------------------------------------------------

  table: {
    width: "100%",
    border: "1px solid #dee2e6",
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f1f3f9",
    borderBottom: "1px solid #dee2e6",
    paddingVertical: 7,
    paddingHorizontal: 6,
  },

  tableRow: {
    flexDirection: "row",
    borderBottom: "1px solid #eeeeee",
    paddingVertical: 7,
    paddingHorizontal: 6,
    minHeight: 30,
  },

  // ----------------------------------------------------------
  // TABLE COLUMNS
  // ----------------------------------------------------------

  accountColumn: {
    width: "19%",
  },

  balanceColumn: {
    width: "15%",
  },

  disbursementColumn: {
    width: "15%",
  },

  payableColumn: {
    width: "17%",
  },

  receivableColumn: {
    width: "17%",
  },

  availableColumn: {
    width: "17%",
  },

  // ----------------------------------------------------------
  // TABLE TEXT
  // ----------------------------------------------------------

  tableHeaderText: {
    fontSize: 6.5,
    fontWeight: 700,
    color: "#6c757d",
  },

  tableText: {
    fontSize: 7.5,
    color: "#212529",
  },

  accountText: {
    fontSize: 7.5,
    fontWeight: 600,
    color: "#212529",
  },

  // ----------------------------------------------------------
  // FOOTER
  // ----------------------------------------------------------

  footer: {
    position: "absolute",
    bottom: 20,
    left: 30,
    right: 30,
    textAlign: "center",
    fontSize: 7,
    color: "#999999",
  },
});

// ============================================================
// HELPERS
// ============================================================

const formatCurrency = (value, currency = "PHP") => {
  const amount = Number(value) || 0;

  const symbolMap = {
    PHP: "₱",
    USD: "$",
    EUR: "€",
    GBP: "£",
    JPY: "¥",
    CNY: "¥",
    SGD: "S$",
    HKD: "HK$",
    AUD: "A$",
    CAD: "C$",
  };

  const symbol = symbolMap[currency] || currency;

  return `${symbol}${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (date) => {
  if (!date) {
    return "";
  }

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");

  const day = String(parsedDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

// ============================================================
// PDF COMPONENT
// ============================================================

const BankBudgetingPDF = ({
  selectedCutoff,
  selectedAccounts = [],
  getJournal = {
    receivables: 0,
    payables: 0,
  },
  totalAllocatedReceivables = 0,
  totalAllocatedPayables = 0,
}) => {
  // ----------------------------------------------------------
  // SUMMARY CALCULATIONS
  // ----------------------------------------------------------

  const remainingReceivables =
    Number(getJournal.receivables || 0) -
    Number(totalAllocatedReceivables || 0);

  const remainingPayables =
    Number(getJournal.payables || 0) - Number(totalAllocatedPayables || 0);

  // ----------------------------------------------------------
  // AVAILABLE FUNDS
  // Same calculation as your main component
  // ----------------------------------------------------------

  const calculateAvailableFunds = (account) => {
    const accountCurrencyName = account.currency_name;

    const currencyData = selectedCutoff?.currency_rates?.find(
      (data) => data.currency?.currency_name === accountCurrencyName,
    );

    const currencyRate = Number(currencyData?.currency_rate) || 1;

    const balance = Number(account.amount) || 0;

    const disbursement = Number(account.disbursementSumPayables) || 0;

    const payablesPHP = Number(account.allocated_payables) || 0;

    const receivablesPHP = Number(account.allocated_receivables) || 0;

    const payables =
      accountCurrencyName === "PHP" ? payablesPHP : payablesPHP / currencyRate;

    const receivables =
      accountCurrencyName === "PHP"
        ? receivablesPHP
        : receivablesPHP / currencyRate;

    return balance - disbursement - payables + receivables;
  };

  // ----------------------------------------------------------
  // RENDER
  // ----------------------------------------------------------

  return (
    <Document
      title="Bank Budgeting - Fund Forecasting"
      author="Bank Budgeting"
      subject="Bank Budgeting Fund Forecasting"
    >
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* ==================================================
            HEADER
        ================================================== */}

        <Text style={styles.title}>BANK BUDGETING</Text>

        <Text style={styles.subtitle}>Fund Forecasting</Text>

        {/* ==================================================
            ACCOUNTING PERIOD
        ================================================== */}

        <View style={styles.periodContainer}>
          <Text style={styles.sectionTitle}>ACCOUNTING PERIOD</Text>

          <View style={styles.periodRow}>
            <View style={styles.periodColumn}>
              <Text style={styles.label}>Cutoff</Text>

              <Text style={styles.value}>{selectedCutoff?.cutoff_name}</Text>
            </View>

            <View style={styles.periodColumn}>
              <Text style={styles.label}>Until</Text>

              <Text style={styles.value}>
                {formatDate(selectedCutoff?.cutoff_to)}
              </Text>
            </View>
          </View>

          {/* ----------------------------------------------
              CURRENCY RATES
          ---------------------------------------------- */}

          <View style={styles.currencySection}>
            <Text style={styles.sectionTitle}>CURRENCY RATES</Text>

            <View style={styles.currencyRow}>
              {(selectedCutoff?.currency_rates || []).map((data, index) => {
                const currencyName = data.currency?.currency_name || "";

                const currencyRate = Number(data.currency_rate) || 0;

                return (
                  <View
                    key={data.id || `${currencyName}-${index}`}
                    style={styles.currencyBox}
                  >
                    <Text>
                      <Text style={styles.currencyName}>{currencyName}</Text>

                      <Text style={styles.currencyEquals}>{" = "}</Text>

                      <Text style={styles.currencyValue}>
                        ₱
                        {currencyRate.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Text>
                    </Text>
                  </View>
                );
              })}
            </View>
          </View>
        </View>

        {/* ==================================================
            SUMMARY
        ================================================== */}

        <View style={styles.summaryContainer}>
          {/* RECEIVABLES */}

          <View style={[styles.summaryBox, styles.summaryBoxLeft]}>
            <Text style={styles.summaryLabel}>RECEIVABLE COLLECTION</Text>

            <Text style={styles.summaryValue}>
              {formatCurrency(remainingReceivables, "PHP")}
              {" of "}
              {formatCurrency(getJournal.receivables, "PHP")}
            </Text>
          </View>

          {/* PAYABLES */}

          <View style={[styles.summaryBox, styles.summaryBoxRight]}>
            <Text style={styles.summaryLabel}>PAYABLES</Text>

            <Text style={styles.summaryValue}>
              {formatCurrency(remainingPayables, "PHP")}
              {" of "}
              {formatCurrency(getJournal.payables, "PHP")}
            </Text>
          </View>
        </View>

        {/* ==================================================
            ACCOUNT TABLE
        ================================================== */}

        <View style={styles.table}>
          {/* ----------------------------------------------
              TABLE HEADER
          ---------------------------------------------- */}

          <View style={styles.tableHeader}>
            <Text style={[styles.accountColumn, styles.tableHeaderText]}>
              ACCOUNT NAME
            </Text>

            <Text style={[styles.balanceColumn, styles.tableHeaderText]}>
              CURRENT BALANCE
            </Text>

            <Text style={[styles.disbursementColumn, styles.tableHeaderText]}>
              DISBURSEMENT
            </Text>

            <Text style={[styles.payableColumn, styles.tableHeaderText]}>
              ALLOCATE PAYABLES
            </Text>

            <Text style={[styles.receivableColumn, styles.tableHeaderText]}>
              ALLOCATE RECEIVABLES
            </Text>

            <Text style={[styles.availableColumn, styles.tableHeaderText]}>
              AVAILABLE FUNDS
            </Text>
          </View>

          {/* ----------------------------------------------
              TABLE BODY
          ---------------------------------------------- */}

          {selectedAccounts.map((account, index) => {
            const currency = account.currency_name || "PHP";

            const availableFunds = calculateAvailableFunds(account);

            return (
              <View
                key={account.id || index}
                style={styles.tableRow}
                wrap={false}
              >
                {/* ACCOUNT NAME */}

                <Text style={[styles.accountColumn, styles.accountText]}>
                  {account.account_name}
                </Text>

                {/* CURRENT BALANCE */}

                <Text style={[styles.balanceColumn, styles.tableText]}>
                  {formatCurrency(account.amount, currency)}
                </Text>

                {/* DISBURSEMENT */}

                <Text style={[styles.disbursementColumn, styles.tableText]}>
                  {formatCurrency(account.disbursementSumPayables, currency)}
                </Text>

                {/* ALLOCATE PAYABLES */}

                <Text style={[styles.payableColumn, styles.tableText]}>
                  {formatCurrency(account.allocated_payables, "PHP")}
                </Text>

                {/* ALLOCATE RECEIVABLES */}

                <Text style={[styles.receivableColumn, styles.tableText]}>
                  {formatCurrency(account.allocated_receivables, "PHP")}
                </Text>

                {/* AVAILABLE FUNDS */}

                <Text style={[styles.availableColumn, styles.tableText]}>
                  {formatCurrency(availableFunds, currency)}
                </Text>
              </View>
            );
          })}
        </View>

        {/* ==================================================
            FOOTER
        ================================================== */}

        <Text
          style={styles.footer}
          fixed
          render={({ pageNumber, totalPages }) =>
            `Bank Budgeting • Page ${pageNumber} of ${totalPages}`
          }
        />
      </Page>
    </Document>
  );
};

export default BankBudgetingPDF;
